/* ==========================================================================
   we-pdp.js - New PDP behaviour (STATIC PHASE)
   --------------------------------------------------------------------------
   Reproduces the interactions defined in the client design only. It does NOT
   touch the cart, the Shopify variant model, subscriptions or any review app;
   those arrive in the dynamic phase.

   Constraints honoured here:
     - no globals beyond the single WePdp namespace
     - no inline event handlers in the markup
     - event delegation on document, bound exactly once
     - idempotent: safe to run again after a theme-editor section reload
   ========================================================================== */
(function () {
  'use strict';

  if (window.WePdp && window.WePdp.bound) {
    window.WePdp.refresh();
    return;
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function setText(sel, value, ctx) {
    var el = qs(sel, ctx);
    if (el) el.textContent = value;
  }

  function once(el, flag) {
    if (!el || el.dataset[flag] === 'true') return false;
    el.dataset[flag] = 'true';
    return true;
  }

  /* ------------------------------------------------------------------------
     Scroll lock (shared by all three dialogs)
     ------------------------------------------------------------------------ */
  var lockCount = 0;
  var savedOverflow = '';

  function lockScroll() {
    if (lockCount === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = savedOverflow;
  }

  /* ------------------------------------------------------------------------
     Dialogs
     ------------------------------------------------------------------------ */
  var lastFocused = null;

  function openDialog(id) {
    var overlay = document.getElementById(id);
    if (!overlay || overlay.classList.contains('we-pdp__overlay--open')) return;
    lastFocused = document.activeElement;
    overlay.classList.add('we-pdp__overlay--open');
    overlay.removeAttribute('aria-hidden');
    lockScroll();
    /* Detected by content, not by a hardcoded id string: the Facts dialog is
       the only one with a carousel inside it, and its own id is now
       section-scoped (we-pdp-facts-dialog-{{ section.id }}), so it can no
       longer be matched by a fixed literal here. */
    if (qs('[data-wep-facts-carousel]', overlay)) resetFactsCarousel();
    var focusable = qs('[data-wep-dialog-close]', overlay);
    if (focusable) focusable.focus();
  }

  function closeDialog(overlay) {
    if (!overlay || !overlay.classList.contains('we-pdp__overlay--open')) return;
    overlay.classList.remove('we-pdp__overlay--open');
    overlay.setAttribute('aria-hidden', 'true');

    /* Stop and rewind any media in this dialog, so a video can never keep
       playing unseen after the dialog is dismissed. Setting currentTime can
       throw before metadata has loaded, hence the guard. */
    qsa('video, audio', overlay).forEach(function (media) {
      media.pause();
      try {
        media.currentTime = 0;
      } catch (e) {}
    });

    unlockScroll();
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function closeAllDialogs() {
    qsa('.we-pdp__overlay--open').forEach(closeDialog);
  }

  /* ------------------------------------------------------------------------
     Supplement Facts carousel
     ------------------------------------------------------------------------
     Works against however many [data-wep-facts-slide] elements are rendered
     - the test phase has two (the same image twice), the dynamic phase's
     product metafield may have one or several. Nothing here assumes a count.
     ------------------------------------------------------------------------ */
  function factsCarouselRoot() {
    return qs('[data-wep-facts-carousel]');
  }

  function factsTrack(root) {
    return qs('[data-wep-facts-track]', root);
  }

  /* Scrolling the track IS the navigation - the slides are a native
     scroll-snap strip (see we-pdp-media-dialog.css), which is the same
     mechanism the Gallery stage uses and the reason touch/swipe works here
     without a single gesture handler. Mirrors scrollStageToIndex() in
     assets/we-pdp-gallery.js, including its early return: re-scrolling to a
     position the track is already at would fight the user's own fling.
     `instant` is used when the dialog opens, so reopening never animates. */
  function scrollFactsToIndex(root, index, instant) {
    var track = factsTrack(root);
    if (!track) return;

    var width = track.clientWidth;
    if (!width) return;

    var target = index * width;
    if (Math.abs(track.scrollLeft - target) < 1) return;

    if (!instant && track.scrollTo) {
      track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      track.scrollLeft = target;
    }
  }

  /* opts.scroll === false is how the scroll listener below reports a position
     the user reached by swiping: state and chrome update, but the track is not
     told to move again. Same guard shape as the Gallery's opts.scrollStage. */
  function goToFactsSlide(root, index, opts) {
    var slides = qsa('[data-wep-facts-slide]', root);
    var total = slides.length;
    if (!total) return;
    var clamped = Math.max(0, Math.min(index, total - 1));

    var prevBtn = qs('[data-wep-facts-prev]', root);
    var nextBtn = qs('[data-wep-facts-next]', root);
    if (prevBtn) prevBtn.disabled = clamped === 0;
    if (nextBtn) nextBtn.disabled = clamped === total - 1;

    var current = qs('[data-wep-facts-current]', root);
    if (current) current.textContent = String(clamped + 1);

    root.dataset.wepFactsIndex = String(clamped);

    if (!opts || opts.scroll !== false) {
      scrollFactsToIndex(root, clamped, opts && opts.instant);
    }
  }

  /* Reads the user's own swipe back out of the track so the counter and the
     Prev/Next disabled states stay correct. rAF-coalesced and passive, and
     bound once per track - mirrors initStageScrollSync() in
     assets/we-pdp-gallery.js. dataset.wepFactsIndex remains the single source
     of truth for which slide is current; nothing here keeps a second copy. */
  function initFactsScrollSync(root) {
    var track = factsTrack(root);
    if (!track || track.dataset.wepFactsSync === 'true') return;
    track.dataset.wepFactsSync = 'true';

    var pending = false;

    track.addEventListener('scroll', function () {
      if (pending) return;
      pending = true;

      window.requestAnimationFrame(function () {
        pending = false;

        var width = track.clientWidth;
        if (!width) return;

        var index = Math.round(track.scrollLeft / width);
        if (String(index) === root.dataset.wepFactsIndex) return;

        goToFactsSlide(root, index, { scroll: false });
      });
    }, { passive: true });
  }

  function stepFactsCarousel(root, delta) {
    var current = parseInt(root.dataset.wepFactsIndex || '0', 10);
    goToFactsSlide(root, current + delta);
  }

  function resetFactsCarousel() {
    var root = factsCarouselRoot();
    if (!root) return;
    initFactsScrollSync(root);
    goToFactsSlide(root, 0, { instant: true });
  }

  /* ------------------------------------------------------------------------
     Voices Of Satisfaction video dialog
     ------------------------------------------------------------------------
     One shared dialog, populated per click from whichever card was opened -
     same "populate a shared dialog from the clicked element's data" pattern
     as the Study modal below, just for a single video URL instead of several
     text fields. A dedicated [data-wep-vos-open] attribute (not the generic
     [data-wep-dialog-open] every other dialog trigger uses) is what lets this
     be intercepted here first, so the video is swapped in before the dialog
     opens. closeDialog()'s existing generic pause/rewind-on-close logic
     already covers this <video>, since it targets any video/audio inside
     whichever dialog just closed - nothing here duplicates that.
     ------------------------------------------------------------------------ */
  /* Fix: custom.meta_video_testimonials.video_link entries are not all
     actually .mp4 - some of this metaobject's existing Shopify-hosted
     uploads are .mov (confirmed live via the file's own Content-Type:
     video/quicktime). The <source> below only ever declared a hardcoded
     type="video/mp4", so browsers discarded any non-mp4 URL before even
     requesting it (video.networkState stayed NETWORK_NO_SOURCE) - the
     dialog opened correctly but its video never appeared, independent of
     the section-id/modal-id fix.

     Confirmed live: this is not fixable by declaring type="video/quicktime"
     instead - Chrome's canPlayType('video/quicktime') is '' (no QuickTime
     demuxer at all), even though the .mov file's actual video/audio codecs
     inside are ones Chrome decodes fine. Only omitting the type attribute
     (so the browser fetches and sniffs the real content instead of
     trusting a container guess from the extension) loads it successfully -
     verified live against the actual failing file. mp4/webm/ogg keep an
     explicit type (lets the browser skip a source it truly cannot decode
     without a request); anything else falls back to type-less sniffing. */
  function videoMimeType(url) {
    var ext = String(url || '').split(/[#?]/)[0].split('.').pop().toLowerCase();
    if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
    if (ext === 'webm') return 'video/webm';
    if (ext === 'ogv') return 'video/ogg';
    return null;
  }

  function openVosDialog(trigger) {
    var card = trigger.closest('.we-pdp__vos-card');
    /* Found by its own class, not a hardcoded id - the id is now
       section-scoped (we-pdp-video-dialog-{{ section.id }}), and there is
       only ever one .we-pdp__vid-overlay on the page regardless. */
    var overlay = qs('.we-pdp__vid-overlay');
    if (!card || !overlay) return;

    var data = qs('[data-wep-vos-data]', card);
    var video = qs('[data-wep-vos-video]', overlay);
    if (data && video) {
      var source = qs('source', video);
      var url = data.dataset.videoUrl || '';
      if (source) {
        source.setAttribute('src', url);
        var mime = videoMimeType(url);
        if (mime) {
          source.setAttribute('type', mime);
        } else {
          source.removeAttribute('type');
        }
      }
      video.load();
    }

    openDialog(overlay.id);
  }

  /* Study dialog is populated from markup, so the content stays in Liquid.
     DYNAMIC PHASE: study_design/publication_meta are each optional per study
     - a blank one must hide just that element (not leave an empty paragraph
     taking up space), so both use setOptionalText rather than setText. */
  function setOptionalText(sel, value, ctx) {
    var el = qs(sel, ctx);
    if (!el) return;
    var text = value || '';
    el.textContent = text;
    el.hidden = !text;
  }

  function openStudyDialog(trigger) {
    var source = trigger.closest('[data-wep-study]');
    /* Found by its inner dialog's own class, not a hardcoded id - the id is
       now section-scoped (we-pdp-study-dialog-{{ section.id }}), and there
       is only ever one .we-pdp__study-dialog on the page regardless. */
    var studyDialog = qs('.we-pdp__study-dialog');
    var overlay = studyDialog && studyDialog.closest('.we-pdp__overlay');
    if (!source || !overlay) return;

    var data = qs('[data-wep-study-data]', source);
    if (data) {
      setText('[data-wep-study-title]', data.dataset.title || '', overlay);
      setText('[data-wep-study-blurb]', data.dataset.blurb || '', overlay);
      setOptionalText('[data-wep-study-design]', data.dataset.design, overlay);
      setOptionalText('[data-wep-study-meta]', data.dataset.meta, overlay);

      var list = qs('[data-wep-study-evidence]', overlay);
      if (list) {
        list.textContent = '';
        qsa('[data-wep-study-point]', data).forEach(function (point) {
          var li = document.createElement('li');
          li.textContent = point.textContent.trim();
          list.appendChild(li);
        });
      }

      var evidenceSection = qs('.we-pdp__study-ev', overlay);
      if (evidenceSection) {
        evidenceSection.hidden = !(list && list.children.length > 0);
      }

      /* DYNAMIC PHASE: study_url is now an optional per-study field - a card
         with no URL must hide this CTA rather than link to "#", so presence
         of a real URL (not just a truthy string) gates the link itself. */
      var link = qs('[data-wep-study-link]', overlay);
      if (link) {
        var url = data.dataset.url || '';
        if (url) {
          link.hidden = false;
          link.setAttribute('href', url);
        } else {
          link.hidden = true;
        }
      }
    }

    openDialog(overlay.id);
  }

  /* ------------------------------------------------------------------------
     Accordion
     ------------------------------------------------------------------------ */
  function setPanelHeight(acc) {
    var panel = qs('.we-pdp__acc-panel', acc);
    if (!panel) return;
    panel.style.maxHeight = acc.classList.contains('we-pdp__acc--open')
      ? panel.scrollHeight + 'px'
      : '0px';
  }

  function closeAccordion(acc) {
    if (!acc.classList.contains('we-pdp__acc--open')) return;
    acc.classList.remove('we-pdp__acc--open');
    var head = qs('.we-pdp__acc-head', acc);
    if (head) head.setAttribute('aria-expanded', 'false');
    setPanelHeight(acc);
  }

  /* Accordions allow multiple panels open by default. A container marked
     [data-wep-accordion-single] opts into single-open instead: opening one of
     its accordions collapses its open sibling(s) within that same container.

     Both the FAQ list (we-pdp-faq.liquid) and the shared buy-box accordion
     group (we-pdp-buybox-accordions.liquid) opt into this. */
  function toggleAccordion(head) {
    var acc = head.closest('.we-pdp__acc');
    if (!acc) return;

    var willOpen = !acc.classList.contains('we-pdp__acc--open');
    var singleOpenGroup = willOpen ? acc.closest('[data-wep-accordion-single]') : null;

    if (singleOpenGroup) {
      qsa('.we-pdp__acc--open', singleOpenGroup).forEach(function (openAcc) {
        if (openAcc === acc) return;
        /* Ignore anything belonging to a nested single-open group. */
        if (openAcc.closest('[data-wep-accordion-single]') !== singleOpenGroup) return;
        closeAccordion(openAcc);
      });
    }

    acc.classList.toggle('we-pdp__acc--open', willOpen);
    head.setAttribute('aria-expanded', String(willOpen));
    setPanelHeight(acc);
  }

  function syncOpenAccordions() {
    qsa('.we-pdp__acc--open').forEach(setPanelHeight);
  }

  /* ------------------------------------------------------------------------
     Gallery + Variant selection
     ------------------------------------------------------------------------
     Both now live entirely in their own files (we-pdp-gallery.js,
     we-pdp-variants.js) - own bound listeners, own delegated events, each
     loaded by its own snippet. assets/we-pdp-variants.js is the single
     authoritative resolver for "which Shopify variant is selected" (option
     rendering, availability, URL sync, dispatching "wep:variant-change");
     this file no longer resolves a variant itself, it only listens for
     that one event, same as Gallery does. Splitting Gallery out was
     previously deferred as too risky for the shared-closure design, but
     the variant-change event this task's predecessor required gave both
     Gallery and now Variants a natural, already-decoupled boundary to move
     behind, and keeps this file within the agreed 400-500 line guideline
     even as more sections come online.

     Pricing & Purchase Mode (the "how much" half of the buy-box - one-time
     price, Subscribe & Save, delivery frequency) now lives entirely in
     assets/we-pdp-pricing.js, its own "wep:variant-change" listener with
     the same natural, already-decoupled boundary as Gallery/Variants. The
     former PACK_COPY constant here was dead code (defined, never read)
     left over from before the option UI became dynamic, and pack
     merchandising copy now lives entirely in assets/we-pdp-variants.js,
     sourced from the variant's own metafields - removed rather than left
     behind as a hidden fallback. */

  /* ------------------------------------------------------------------------
     Sticky add-to-cart bar
     ------------------------------------------------------------------------ */
  /* The theme's mobile bottom navigation is fixed at bottom:0 with z-index:99,
     above this bar's z-index:90, so it would cover the bar's lower half. Its
     height is measured rather than assumed: the theme sets min-height:60px but
     the rendered height varies with the merchant's font-size and icon-size
     settings, and the section can be absent entirely. It is display:none above
     1024px, so the measurement is naturally 0 on desktop. */
  var BOTTOM_NAV_SELECTOR = '.halo-sticky-toolbar-mobile, .halo-sticky-toolbar-mobile .bottom-bar';

  function measureBottomNav(bar) {
    var height = 0;

    qsa(BOTTOM_NAV_SELECTOR).forEach(function (el) {
      var style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      height = Math.max(height, el.getBoundingClientRect().height);
    });

    height = Math.round(height);
    bar.style.setProperty('--wep-bottom-nav-h', height + 'px');
    /* Also on body (inherited by --wep-sticky-h's other consumers, e.g. the
       toast below) - the bar's own inline property isn't visible outside it. */
    document.body.style.setProperty('--wep-bottom-nav-h', height + 'px');
    if (height > 0) {
      bar.setAttribute('data-wep-above-nav', 'true');
    } else {
      bar.removeAttribute('data-wep-above-nav');
    }
  }

  /* Clearance so the fixed bar cannot sit on top of the last thing on the
     page. Applied to <body>, because that is the only element that is below
     all page content - the design put it in its own footer, which is theme
     chrome we do not render. Sized from the bar's rendered height and only
     while the bar is on screen. The theme reserves its own room for the
     mobile navigation (.sticky-toolbar-mobile .footer), so that height is
     deliberately not added here. */
  function applyStickyClearance(bar, isShown) {
    var body = document.body;
    body.style.setProperty('--wep-sticky-h', Math.round(bar.getBoundingClientRect().height) + 'px');
    body.classList.toggle('we-pdp-has-sticky-bar', isShown);
  }

  function updateStickyBar() {
    var bar = qs('[data-wep-sticky]');
    var anchor = qs('[data-wep-atc-anchor]');
    if (!bar || !anchor) return;
    measureBottomNav(bar);
    var isShown = anchor.getBoundingClientRect().bottom < 8;
    bar.classList.toggle('we-pdp-sticky--show', isShown);
    applyStickyClearance(bar, isShown);
  }

  /* ------------------------------------------------------------------------
     Toast
     ------------------------------------------------------------------------ */
  var toastTimer = null;

  function showToast(message) {
    var toast = qs('[data-wep-toast]');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.classList.add('we-pdp__toast--show');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toast.classList.remove('we-pdp__toast--show');
    }, 1800);
  }

  /* ------------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------------ */
  var revealObserver = null;

  function initReveal() {
    var items = qsa('.we-pdp__reveal').filter(function (el) {
      return once(el, 'wepReveal');
    });

    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('we-pdp__reveal--in');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('we-pdp__reveal--in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    items.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Sticky gallery offset - measured from the real theme header
     ------------------------------------------------------------------------ */
  /* This theme pins its header by adding .shopify-section-header-sticky to the
     header SECTION wrapper on scroll (base.css: position:sticky !important;
     top:0) - not to <header> or .header-wrapper, which stay static. Measuring
     only those generic selectors always found nothing, so the gallery kept the
     96px design fallback regardless of the real header height. The theme adds
     that class during scrolling, so this re-runs on scroll. */
  var HEADER_SELECTOR = [
    '.shopify-section-header-sticky',
    '#shopify-section-header',
    'header',
    '.header',
    '#header',
    '[data-header]',
    '.header-wrapper'
  ].join(',');

  var lastStickyOffset = null;

  function measureStickyOffset() {
    if (!qs('.we-pdp')) return;

    var height = 0;

    qsa(HEADER_SELECTOR).forEach(function (el) {
      var style = window.getComputedStyle(el);
      if (style.position !== 'sticky' && style.position !== 'fixed') return;
      /* Only count boxes actually pinned to the top of the viewport. Guards
         against unrelated sticky furniture such as .halo-widget-banner,
         which is sticky at top: 55%. */
      if (parseFloat(style.top) > 8) return;
      var rect = el.getBoundingClientRect();
      if (rect.height === 0 || rect.top > 8) return;
      height = Math.max(height, rect.height);
    });

    /* Clamped so an unexpected match can never push the gallery off-screen. */
    var offset = height ? Math.min(Math.round(height + 24), 200) : 96;
    if (offset === lastStickyOffset) return;
    lastStickyOffset = offset;

    qsa('.we-pdp').forEach(function (section) {
      section.style.setProperty('--wep-sticky-top', offset + 'px');
    });
  }

  /* ------------------------------------------------------------------------
     Delegated events - bound once
     ------------------------------------------------------------------------ */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var accHead = target.closest('.we-pdp__acc-head');
    if (accHead) {
      toggleAccordion(accHead);
      return;
    }

    var studyCta = target.closest('[data-wep-study-open]');
    if (studyCta) {
      openStudyDialog(studyCta);
      return;
    }

    var vosOpen = target.closest('[data-wep-vos-open]');
    if (vosOpen) {
      openVosDialog(vosOpen);
      return;
    }

    var dialogOpen = target.closest('[data-wep-dialog-open]');
    if (dialogOpen) {
      openDialog(dialogOpen.dataset.wepDialogOpen);
      return;
    }

    var dialogClose = target.closest('[data-wep-dialog-close]');
    if (dialogClose) {
      closeDialog(dialogClose.closest('.we-pdp__overlay'));
      return;
    }

    var factsPrev = target.closest('[data-wep-facts-prev]');
    if (factsPrev) {
      var prevRoot = factsPrev.closest('[data-wep-facts-carousel]');
      if (prevRoot) stepFactsCarousel(prevRoot, -1);
      return;
    }

    var factsNext = target.closest('[data-wep-facts-next]');
    if (factsNext) {
      var nextRoot = factsNext.closest('[data-wep-facts-carousel]');
      if (nextRoot) stepFactsCarousel(nextRoot, 1);
      return;
    }

    /* Clicking the backdrop, but not the dialog itself. */
    if (target.classList && target.classList.contains('we-pdp__overlay')) {
      closeDialog(target);
      return;
    }

    var edit = target.closest('[data-wep-sticky-edit]');
    if (edit) {
      var anchor = qs('[data-wep-scroll-target]');
      if (anchor) {
        window.scrollTo({
          top: anchor.getBoundingClientRect().top + window.scrollY - 110,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
      return;
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      closeAllDialogs();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      var delta = event.key === 'ArrowLeft' ? -1 : 1;

      /* Left/Right only steps the Supplement Facts carousel, and only while
         its dialog is open, so this never competes with arrow-key behaviour
         anywhere else on the page. Found via the carousel's own ancestor
         overlay, not a hardcoded dialog id - that id is now section-scoped
         (we-pdp-facts-dialog-{{ section.id }}), and this check needs no id
         at all since factsCarouselRoot() already finds the one carousel on
         the page. */
      var root = factsCarouselRoot();
      var factsDialog = root && root.closest('.we-pdp__overlay');
      if (!factsDialog || !factsDialog.classList.contains('we-pdp__overlay--open')) return;
      event.preventDefault();
      stepFactsCarousel(root, delta);
    }
  }

  /* ------------------------------------------------------------------------
     Init / refresh
     ------------------------------------------------------------------------ */
  function refresh() {
    initReveal();
    measureStickyOffset();
    updateStickyBar();
    syncOpenAccordions();
  }

  function init() {
    refresh();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncOpenAccordions);
    }
  }

  /* One scroll pass: the sticky bar state plus a re-measure of the theme
     header, which only becomes sticky partway down the page. Deliberately not
     requestAnimationFrame-throttled - rAF is suspended whenever the page is
     not compositing (background tab, hidden pane), which would leave the bar
     and the offset stale. Both calls are a handful of rect reads, and
     measureStickyOffset early-returns when the offset has not changed. */
  function onScroll() {
    measureStickyOffset();
    updateStickyBar();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    measureStickyOffset();
    updateStickyBar();
    syncOpenAccordions();
  });

  /* Theme editor: re-measure and re-bind observers after a section reload. */
  document.addEventListener('shopify:section:load', refresh);

  window.WePdp = { bound: true, refresh: refresh, showToast: showToast };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
